import { FC, ReactNode } from 'react';
import type { VendasForecast, VendasMes, VendasPayload } from '@/types/dashboards';
import type { DivergenciaVenda, LinhaSkuVendas } from '@/utils/equiplexVendas';
import { fmtData, fmtDataHora } from '@/utils/painelFormat';
import Aviso from '../Aviso';
import { STATUS } from '../chartTheme';

interface Props {
  frescor: VendasPayload['frescor'];
  forecast: VendasForecast;
  mes: VendasMes;
  corte: string;
  divergencias: DivergenciaVenda[];
}

const codigo = (l: LinhaSkuVendas) => l.codigoDatasul ?? l.codigo;

function avisoForecast(forecast: VendasForecast, mes: VendasMes): ReactNode {
  if (forecast.status === 'indisponivel') {
    return (
      <Aviso key="forecast" testId="aviso-forecast-indisponivel" icone="sync_problem" cor={STATUS.critico} titulo="Forecast não carregou">
        {forecast.mensagem ?? 'Não foi possível ler o Balanceamento de Linha.'} O faturado e as UFs seguem valendo.
      </Aviso>
    );
  }
  if (!mes.tem_forecast) {
    return (
      <Aviso key="forecast" testId="aviso-sem-forecast" icone="event_busy" cor={STATUS.atencao} titulo={`${mes.label} sem forecast`}>
        O Balanceamento de Linha não tem demanda para este mês ({mes.range}). O faturado aparece sem comparação até o
        forecast do comercial ser importado lá.
      </Aviso>
    );
  }
  return null;
}

/** O que muda a leitura dos números: réplica parada, forecast ausente, mês aberto, código trocado. */
const AvisosVendas: FC<Props> = ({ frescor, forecast, mes, corte, divergencias }) => {
  const avisos: ReactNode[] = [];
  if (frescor.replica_atrasada) {
    avisos.push(
      <Aviso key="frescor" testId="aviso-replica" icone="cloud_off" cor={STATUS.critico} titulo="Réplica do Datasul parada">
        A última carga foi em {fmtDataHora(frescor.ultima_carga_replica)}
        {frescor.dias_sem_carga !== null && <> ({frescor.dias_sem_carga} dias)</>}. O faturado vai só até{' '}
        {fmtData(frescor.faturamento_ate)}.
      </Aviso>,
    );
  }
  const prev = avisoForecast(forecast, mes);
  if (prev) avisos.push(prev);
  if (mes.em_andamento) {
    avisos.push(
      <Aviso key="parcial" testId="aviso-parcial" icone="hourglass_top" cor={STATUS.atencao} titulo={`${mes.label} ainda está aberto`}>
        Notas até {fmtData(corte)}: {mes.dias_decorridos} de {mes.dias} dias. O ritmo compara o faturado com o forecast
        proporcional aos dias que já passaram.
      </Aviso>,
    );
  }
  if (divergencias.length > 0) {
    avisos.push(
      <Aviso key="codigo" testId="aviso-codigo" icone="swap_horiz" cor={STATUS.atencao} titulo="Forecast e venda em códigos diferentes">
        {divergencias.map((d, i) => (
          <span key={d.categoria}>
            {i > 0 && '; '}
            <strong>{d.categoria}</strong>: o forecast cita {d.semVenda.map(codigo).join(', ')}, sem venda, e as notas saíram
            em {d.foraDoForecast.map(codigo).join(', ')}, fora do forecast
          </span>
        ))}
        . Parece troca de código no cadastro do Balanceamento; o total do formato não é afetado.
      </Aviso>,
    );
  }
  if (avisos.length === 0) return null;
  return <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">{avisos}</div>;
};

export default AvisosVendas;
